import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ConnectedPoints = ({ points, onViewOnMap }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filterOptions = [
    { id: 'all', label: 'All Points', icon: 'Grid3X3', count: points?.length },
    { id: 'cultural', label: 'Cultural', icon: 'Building2', count: points?.filter(p => p?.type === 'cultural')?.length },
    { id: 'natural', label: 'Natural', icon: 'Trees', count: points?.filter(p => p?.type === 'natural')?.length },
    { id: 'tourism', label: 'Tourism', icon: 'Bed', count: points?.filter(p => p?.type === 'tourism')?.length }
  ];

  const filteredPoints = activeFilter === 'all' 
    ? points 
    : points?.filter(point => point?.type === activeFilter);

  const getTypeColor = (type) => {
    switch (type) {
      case 'cultural': return 'text-accent bg-accent/10 border-accent/20';
      case 'natural': return 'text-success bg-success/10 border-success/20';
      case 'tourism': return 'text-warning bg-warning/10 border-warning/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cultural': return 'Building2';
      case 'natural': return 'Trees';
      case 'tourism': return 'Bed';
      default: return 'MapPin';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-semibold text-xl flex items-center gap-2">
          <Icon name="Link" size={20} className="text-primary" />
          Connected Points of Interest
        </h2>
        <Button
          variant="outline"
          size="sm"
          iconName="Map"
          iconPosition="left"
          onClick={onViewOnMap}
        >
          View All on Map
        </Button>
      </div>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filterOptions?.map((option) => (
          <button
            key={option?.id}
            onClick={() => setActiveFilter(option?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-fast ${
              activeFilter === option?.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-muted-foreground/10'
            }`}
          >
            <Icon name={option?.icon} size={16} />
            <span>{option?.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              activeFilter === option?.id
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : 'bg-background text-muted-foreground'
            }`}>
              {option?.count}
            </span>
          </button>
        ))}
      </div>
      {/* Points Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPoints?.map((point) => (
          <div
            key={point?.id}
            className="bg-background rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow duration-fast"
          >
            <div className="aspect-[16/10] relative overflow-hidden">
              <Image
                src={point?.image}
                alt={point?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(point?.type)}`}>
                <div className="flex items-center gap-1">
                  <Icon name={getTypeIcon(point?.type)} size={12} />
                  <span className="capitalize">{point?.type}</span>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <h3 className="font-medium text-base mb-2 line-clamp-1">
                {point?.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {point?.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="MapPin" size={12} />
                  <span className="truncate">{point?.location}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  onClick={() => onViewOnMap(point)}
                  className="text-xs"
                >
                  View
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredPoints?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-medium text-lg mb-2">No points found</h3>
          <p className="text-muted-foreground">
            No {activeFilter === 'all' ? '' : activeFilter} points are connected to this trail.
          </p>
        </div>
      )}
    </div>
  );
};

export default ConnectedPoints;